import type { ChatMessage, ConversationSummary, UserProfileSummary } from "./chatApi";
import { getProfileByUserID } from "./chatApi";

export type ProfileLookup = (userID: string) => Promise<UserProfileSummary>;

const displayNameCache = new Map<string, string | null>();
const avatarReferenceCache = new Map<string, string>();
const inFlightLookups = new Map<string, Promise<boolean>>();
const conversationLabelCache = new Map<string, string>();

const CONVERSATION_LABEL_STORAGE_KEY = "msg.web.conversation_labels.v1";

function normalizeUserID(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized === "" ? null : normalized;
}

function normalizeConversationID(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized === "" ? null : normalized;
}

function normalizeConversationLabel(value: string): string | null {
  const normalized = value.trim();
  return normalized === "" ? null : normalized;
}

function canUseStorage(): boolean {
  try {
    return typeof globalThis.localStorage !== "undefined";
  } catch {
    return false;
  }
}

function persistConversationLabels(): void {
  if (!canUseStorage()) {
    return;
  }

  const payload: Record<string, string> = {};
  for (const [conversationID, label] of conversationLabelCache.entries()) {
    payload[conversationID] = label;
  }

  try {
    globalThis.localStorage.setItem(CONVERSATION_LABEL_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage write failures and keep in-memory behavior.
  }
}

function loadPersistedConversationLabels(): void {
  if (!canUseStorage()) {
    return;
  }

  try {
    const raw = globalThis.localStorage.getItem(CONVERSATION_LABEL_STORAGE_KEY);
    if (raw === null) {
      return;
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    for (const [conversationID, label] of Object.entries(parsed)) {
      const normalizedConversationID = normalizeConversationID(conversationID);
      const normalizedLabel = typeof label === "string" ? normalizeConversationLabel(label) : null;
      if (normalizedConversationID === null || normalizedLabel === null) {
        continue;
      }
      conversationLabelCache.set(normalizedConversationID, normalizedLabel);
    }
  } catch {
    // Ignore malformed storage payloads.
  }
}

function isSafePublicUsernameAlias(value: string): boolean {
  if (value.includes("@") || value.startsWith("+")) {
    return false;
  }
  if (/\s|[/\\]/.test(value)) {
    return false;
  }
  if (/^\d[\d.()_-]*$/.test(value)) {
    return false;
  }
  return value !== "";
}

function normalizePublicUserLabel(profile: UserProfileSummary): string | null {
  const displayName = typeof profile.display_name === "string" ? profile.display_name.trim() : "";
  if (displayName !== "") {
    return displayName;
  }

  const nickname = typeof profile.nickname === "string" ? profile.nickname.trim() : "";
  if (nickname !== "") {
    return nickname;
  }

  const username = typeof profile.username === "string" ? profile.username.trim() : "";
  return isSafePublicUsernameAlias(username) ? username : null;
}

function normalizeAvatarReference(profile: UserProfileSummary): string {
  if (typeof profile.avatar_url !== "string") {
    return "";
  }
  return profile.avatar_url.trim();
}

async function resolveOneUserDisplayName(userID: string, lookup: ProfileLookup): Promise<boolean> {
  if (displayNameCache.has(userID)) {
    return false;
  }

  const existing = inFlightLookups.get(userID);
  if (existing !== undefined) {
    return existing;
  }

  const pending = (async (): Promise<boolean> => {
    try {
      const profile = await lookup(userID);
      displayNameCache.set(userID, normalizePublicUserLabel(profile));
      avatarReferenceCache.set(userID, normalizeAvatarReference(profile));
      return true;
    } catch {
      return false;
    } finally {
      inFlightLookups.delete(userID);
    }
  })();

  inFlightLookups.set(userID, pending);
  return pending;
}

export async function resolveUserDisplayNames(userIDs: Iterable<string>, lookup: ProfileLookup = getProfileByUserID): Promise<boolean> {
  const uniqueIDs = new Set<string>();
  for (const userID of userIDs) {
    const normalized = normalizeUserID(userID);
    if (normalized !== null) {
      uniqueIDs.add(normalized);
    }
  }

  const resolved = await Promise.all([...uniqueIDs].map((userID) => resolveOneUserDisplayName(userID, lookup)));
  return resolved.some((value) => value);
}

function readCachedConversationLabel(conversationID: string): string | null {
  const normalized = normalizeConversationID(conversationID);
  if (normalized === null) {
    return null;
  }
  return conversationLabelCache.get(normalized) ?? null;
}

function readCachedDisplayName(userID: string): string | null | undefined {
  return displayNameCache.get(userID);
}

function readCachedAvatarReference(userID: string): string {
  return avatarReferenceCache.get(userID) ?? "";
}

function formatUserIDLabel(userID: string): string {
  const normalized = normalizeUserID(userID);
  if (normalized === null) {
    return "unknown";
  }

  const cached = readCachedDisplayName(normalized);
  if (cached === null || cached === undefined) {
    return normalized;
  }
  return cached;
}

export function getDirectPeerUserID(conversation: ConversationSummary, actorUserID: string | null): string | null {
  const actor = normalizeUserID(actorUserID);
  const first = normalizeUserID(conversation.participant_a_user_id);
  const second = normalizeUserID(conversation.participant_b_user_id);
  const candidates = [first, second].filter((value): value is string => value !== null);

  if (candidates.length === 0) {
    return null;
  }

  if (actor === null) {
    return candidates[0] ?? null;
  }

  return candidates.find((candidate) => candidate !== actor) ?? candidates[0] ?? null;
}

export function isAvatarReferenceDirectlyRenderable(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized === "") {
    return false;
  }
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("data:")
  );
}

export function buildProfileAvatarBinaryPath(userID: string): string {
  const normalized = normalizeUserID(userID);
  if (normalized === null) {
    return "";
  }
  return `/v1/profiles/${encodeURIComponent(normalized)}/avatar`;
}

export interface DirectPeerAvatarDescriptor {
  peerUserID: string | null;
  label: string;
  avatarReference: string;
  avatarBinaryPath: string | null;
}

export function describeDirectPeerAvatar(
  conversation: ConversationSummary,
  actorUserID: string | null,
): DirectPeerAvatarDescriptor {
  const peerUserID = getDirectPeerUserID(conversation, actorUserID);
  if (peerUserID === null) {
    return {
      peerUserID: null,
      label: "Direct chat",
      avatarReference: "",
      avatarBinaryPath: null,
    };
  }

  const avatarReference = readCachedAvatarReference(peerUserID);
  return {
    peerUserID,
    label: formatUserIDLabel(peerUserID),
    avatarReference,
    avatarBinaryPath:
      avatarReference !== "" && !isAvatarReferenceDirectlyRenderable(avatarReference)
        ? buildProfileAvatarBinaryPath(peerUserID)
        : null,
  };
}

export function describeConversationWithDisplayName(conversation: ConversationSummary, actorUserID: string | null): string {
  if (conversation.type === "direct") {
    const peer = getDirectPeerUserID(conversation, actorUserID);
    if (peer === null) {
      return "Direct chat";
    }
    return `Direct • ${formatUserIDLabel(peer)}`;
  }

  if (conversation.type === "group" || conversation.type === "channel") {
    const backendLabel = normalizeConversationLabel(conversation.name ?? "");
    if (backendLabel !== null) {
      return backendLabel;
    }
  }

  const customConversationLabel = readCachedConversationLabel(conversation.chat_id);
  if (customConversationLabel !== null) {
    return customConversationLabel;
  }

  switch (conversation.type) {
    case "group":
      return "Group chat";
    case "channel":
      return "Channel";
    case "secret":
      return "Secret chat";
    default:
      return "Conversation";
  }
}

export function setConversationDisplayName(conversationID: string, label: string): void {
  const normalizedConversationID = normalizeConversationID(conversationID);
  if (normalizedConversationID === null) {
    return;
  }

  const normalizedLabel = normalizeConversationLabel(label);
  if (normalizedLabel === null) {
    conversationLabelCache.delete(normalizedConversationID);
    persistConversationLabels();
    return;
  }

  conversationLabelCache.set(normalizedConversationID, normalizedLabel);
  persistConversationLabels();
}

export function collectDirectPeerUserIDs(conversations: ConversationSummary[], actorUserID: string | null): string[] {
  const userIDs = new Set<string>();
  for (const conversation of conversations) {
    if (conversation.type !== "direct") {
      continue;
    }
    const peer = getDirectPeerUserID(conversation, actorUserID);
    if (peer !== null) {
      userIDs.add(peer);
    }
  }
  return [...userIDs];
}

export function collectMembershipUserIDs(conversation: ConversationSummary | null): string[] {
  if (conversation === null) {
    return [];
  }

  const userIDs = new Set<string>();
  for (const membership of conversation.memberships) {
    const userID = normalizeUserID(membership.user_id);
    if (userID !== null) {
      userIDs.add(userID);
    }
  }
  return [...userIDs];
}

export function collectMessageSenderUserIDs(messages: ChatMessage[]): string[] {
  const userIDs = new Set<string>();
  for (const message of messages) {
    const senderUserID = normalizeUserID(message.sender_user_id);
    if (senderUserID !== null) {
      userIDs.add(senderUserID);
    }
  }
  return [...userIDs];
}

export function formatSenderLabel(senderUserID: string): string {
  return formatUserIDLabel(senderUserID);
}

export function __resetUserDisplayNameCacheForTests(): void {
  displayNameCache.clear();
  avatarReferenceCache.clear();
  inFlightLookups.clear();
  conversationLabelCache.clear();
  if (canUseStorage()) {
    try {
      globalThis.localStorage.removeItem(CONVERSATION_LABEL_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
  }
}

loadPersistedConversationLabels();
